import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, ButtonGroup, InputGroup, InputGroupText, Spinner} from "reactstrap";
import {AxiosError} from "axios";

import requests from "../requests";
import Autocomplete from "./Autocomplete";
import ShapeSparkline from "./ShapeSparkline";
import {SHAPE_MIN_POINTS} from "../utils/shapeQuery";

// Mirrors SHAPE_MAX_POINTS in the backend's resources/query.py — a `shape` array
// longer than this is rejected with a 400, so a real price series is thinned to
// fit BEFORE it is posted. 5 years of daily closes is ~1,260 sessions and clears
// this comfortably; MAX (~20 years, the full ingested history — see MAX_LOOKBACK_YEARS)
// is ~5,000 sessions and reliably engages the thinning path.
export const SHAPE_MAX_POINTS = 2000;

export const LOOKBACK_PERIODS = ["2W", "1M", "3M", "6M", "1Y", "5Y", "MAX"] as const;
export type LookbackPeriod = (typeof LOOKBACK_PERIODS)[number];

/** How far back "MAX" reaches — comfortably past the ~20 years of history the backend
 * actually ingests (CLAUDE.md), so it always resolves to "everything available" rather
 * than a real cutoff of its own. */
const MAX_LOOKBACK_YEARS = 25;

/** Short button label, and the human phrase used in descriptive copy (error messages,
 * the shape-match comparison note) — kept next to each other since they name the same
 * seven options and would otherwise drift. */
export const LOOKBACK_LABEL: Record<LookbackPeriod, {button: string; phrase: string}> = {
  "2W":  {button: "2W", phrase: "2 weeks"},
  "1M":  {button: "1M", phrase: "1 month"},
  "3M":  {button: "3M", phrase: "3 months"},
  "6M":  {button: "6M", phrase: "6 months"},
  "1Y":  {button: "1Y", phrase: "1 year"},
  "5Y":  {button: "5Y", phrase: "5 years"},
  MAX:   {button: "All", phrase: "full available history"},
};

/** The `from` date for a lookback period, ending at `to`. MAX ignores `to` beyond using
 * it as the anchor year — it's a fixed reach-back, not "since inception" (the backend
 * has no listing-date concept to ask for that), so it just needs to comfortably clear
 * the real ~20-year ingestion window. */
export function startDateFor(period: LookbackPeriod, to: Date): Date {
  const from = new Date(to.getTime());
  switch (period) {
  case "2W": from.setDate(from.getDate() - 14); break;
  case "1M": from.setMonth(from.getMonth() - 1); break;
  case "3M": from.setMonth(from.getMonth() - 3); break;
  case "6M": from.setMonth(from.getMonth() - 6); break;
  case "1Y": from.setFullYear(from.getFullYear() - 1); break;
  case "5Y": from.setFullYear(from.getFullYear() - 5); break;
  case "MAX": from.setFullYear(from.getFullYear() - MAX_LOOKBACK_YEARS); break;
  }
  return from;
}

export interface TickerSeriesMeta {
  ticker: string;
  lookbackPeriod: LookbackPeriod;
  /** First/last session actually returned (may be later/earlier than the requested range). */
  from: string;
  to: string;
  /** Sessions returned by the API, before thinning. */
  sessions: number;
  /** Points actually posted as the `shape` array (== sessions unless thinned). */
  points: number;
  /** The REAL close prices (thinned, but never 0-100-rescaled), same length/order
   * as `realDates` and as the posted `shape` -- sent alongside `shape` so the
   * result can show what the security actually traded at instead of the scaled
   * stand-in every input mode matches on. See DrawShapeQuery's onSubmit. */
  realPrices: number[];
  /** Real trading dates paired 1:1 with `realPrices`. */
  realDates: string[];
}

interface Props {
  /** Same contract as ShapeCanvas/ShapeImageUpload's onChange — values normalized
   * 0-100 (0 = bottom); the backend z-normalizes, so absolute scale never matters.
   * Called with ([], null) on Clear and whenever a load fails. */
  onChange: (shape: number[], meta: TickerSeriesMeta | null) => void;
}

export interface TickerSeriesPickerHandle {
  clear: () => void;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The index positions an evenly-spaced downsample to `maxPoints` would keep --
 * always including index 0 and `length - 1`, so the thinned series still starts
 * and ends where the real one did. Exposed (not just `thinSeries` below) so a
 * PARALLEL array -- e.g. dates alongside prices -- can be thinned with the exact
 * same selection and stay paired 1:1 with the thinned values. */
export function thinIndices(length: number, maxPoints: number): number[] {
  if (length <= maxPoints || maxPoints < 2) {
    return Array.from({length}, (_, i) => i);
  }
  const out: number[] = [];
  for (let i = 0; i < maxPoints; i++) {
    out.push(Math.round((i * (length - 1)) / (maxPoints - 1)));
  }
  return out;
}

/** Evenly-spaced downsample that always keeps the first and last point. */
export function thinSeries(values: number[], maxPoints: number): number[] {
  return thinIndices(values.length, maxPoints).map((i) => values[i]);
}

/** Min-max rescale to the 0-100 range the other two input modes emit. The backend
 * z-normalizes the shape anyway, so this is purely for a consistent contract and a
 * previewable sparkline; a flat series maps to the midline rather than dividing by 0. */
export function normalizeSeries(values: number[]): number[] {
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!(max > min)) return values.map(() => 50);
  return values.map((v) => ((v - min) / (max - min)) * 100);
}

const TickerSeriesPicker = React.forwardRef<TickerSeriesPickerHandle, Props>(
  function TickerSeriesPicker({onChange}, ref) {
    const [tickers, setTickers] = useState<string[]>([]);
    const [ticker, setTicker] = useState<string>("");
    const [lookback, setLookback] = useState<LookbackPeriod>("1Y");
    const [shape, setShape] = useState<number[]>([]);
    const [meta, setMeta] = useState<TickerSeriesMeta | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Latest request wins: a fast lookback toggle can otherwise land an older
    // response after a newer one and show the wrong series next to the meta line.
    const reqIdRef = useRef(0);

    useEffect(() => {
      // Mutable object rather than a `let` flag: the closure genuinely reassigns it on
      // unmount, but a boolean literal narrows to `false` for the type-aware lint rule.
      const live = {value: true};
      void (async () => {
        try {
          const res = await requests.get("/api/tickers");
          if (!live.value) return;
          setTickers(Object.keys((res.data ?? {}) as Record<string, unknown>));
        } catch {
          if (live.value) setError("Unable to load the ticker list. Try again in a moment.");
        }
      })();
      return () => {
        live.value = false;
      };
    }, []);

    const load = useCallback(
      async (symbol: string, period: LookbackPeriod) => {
        const sym = symbol.trim().toUpperCase();
        if (!sym) return;
        const reqId = ++reqIdRef.current;
        setLoading(true);
        setError(null);
        try {
          const to = new Date();
          const from = startDateFor(period, to);
          const res = await requests.get(
            `/api/patterns/price-context?ticker=${encodeURIComponent(sym)}` +
              `&start_date=${isoDate(from)}&end_date=${isoDate(to)}&context_days=0`,
          );
          if (reqId !== reqIdRef.current) return;
          const payload = (res.data ?? {}) as {prices?: number[]; dates?: string[]};
          const rawDates = payload.dates ?? [];
          const rawPrices = payload.prices ?? [];
          // Pair BEFORE filtering -- filtering `prices` alone (the previous behavior)
          // could drop an interior point and silently shift every later index out of
          // alignment with `dates`, which only looked harmless because dates were
          // previously used just for the from/to meta line (first/last elements).
          // Now that real dates travel 1:1 with real prices all the way to the
          // backend, a misaligned pair would mislabel every point after the drop.
          const pairs = rawPrices
            .map((p, i) => ({price: p, date: rawDates[i]}))
            .filter(
              (pt): pt is {price: number; date: string} =>
                typeof pt.price === "number" && isFinite(pt.price) && typeof pt.date === "string",
            );
          if (pairs.length < SHAPE_MIN_POINTS) {
            setShape([]);
            setMeta(null);
            onChange([], null);
            setError(`Not enough price history for ${sym} over the last ${LOOKBACK_LABEL[period].phrase}.`);
            return;
          }
          const idx = thinIndices(pairs.length, SHAPE_MAX_POINTS);
          const realPrices = idx.map((i) => pairs[i].price);
          const realDates = idx.map((i) => pairs[i].date);
          const next = normalizeSeries(realPrices);
          const nextMeta: TickerSeriesMeta = {
            ticker: sym,
            lookbackPeriod: period,
            from: realDates[0] ?? "",
            to: realDates[realDates.length - 1] ?? "",
            sessions: pairs.length,
            points: next.length,
            realPrices,
            realDates,
          };
          setShape(next);
          setMeta(nextMeta);
          onChange(next, nextMeta);
        } catch (err: unknown) {
          if (reqId !== reqIdRef.current) return;
          setShape([]);
          setMeta(null);
          onChange([], null);
          const status = err instanceof AxiosError ? err.response?.status : undefined;
          setError(
            status === 404
              ? `No price history found for ${sym}.`
              : "Couldn't load that price history. Try another symbol.",
          );
        } finally {
          if (reqId === reqIdRef.current) setLoading(false);
        }
      },
      [onChange],
    );

    const clear = useCallback(() => {
      reqIdRef.current++;
      setTicker("");
      setShape([]);
      setMeta(null);
      setError(null);
      setLoading(false);
      onChange([], null);
    }, [onChange]);

    React.useImperativeHandle(ref, () => ({clear}), [clear]);

    const selectLookback = (period: LookbackPeriod) => {
      setLookback(period);
      // Only auto-reload once a series is on screen — otherwise toggling the
      // lookback before picking a symbol would fire a pointless failed request.
      if (meta) void load(meta.ticker, period);
    };

    return (
      <div className="shape-ticker-panel">
        <div className="shape-ticker-controls">
          <InputGroup className="custom-input-group">
            <InputGroupText>Security</InputGroupText>
            <Autocomplete
              data={tickers}
              onChangeProp={setTicker}
              onSelectProp={(selected) => void load(selected, lookback)}
              value={ticker}
            />
          </InputGroup>

          <ButtonGroup size="sm" className="shape-lookback-group">
            {LOOKBACK_PERIODS.map((p) => (
              <Button
                key={p}
                color={p === lookback ? "primary" : "outline-secondary"}
                onClick={() => selectLookback(p)}
              >
                {LOOKBACK_LABEL[p].button}
              </Button>
            ))}
          </ButtonGroup>

          <Button
            color="secondary"
            outline
            size="sm"
            disabled={!ticker.trim() || loading}
            onClick={() => void load(ticker, lookback)}
          >
            {loading ? <Spinner size="sm" /> : "Load history"}
          </Button>
        </div>

        {shape.length > 0 && meta ? (
          <div className="shape-ticker-preview">
            <div className="shape-upload-extracted-label">What we&apos;ll match against:</div>
            <ShapeSparkline shape={shape} />
            <div className="shape-ticker-meta">
              {meta.ticker} · {meta.from} → {meta.to} · {meta.sessions} sessions
              {meta.points < meta.sessions && ` (thinned to ${meta.points} points)`}
            </div>
          </div>
        ) : (
          <div className="shape-ticker-placeholder">
            <p>Pick a security and a lookback to use its real price history as the shape.</p>
            <p className="shape-upload-hint">
              Its closing prices become the query series — no drawing, no digitizing.
            </p>
          </div>
        )}

        {error && <div className="shape-upload-error">{error}</div>}
      </div>
    );
  },
);

export default TickerSeriesPicker;
